import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Edit3,
  KeyRound,
  Loader2,
  LockKeyhole,
  Mail,
  Save,
  ShieldCheck,
  UserRound,
  X,
} from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../components/feedback/ToastProvider";
import { useUnsavedChanges } from "../UnsavedChangesProvider";
import "./AdminAccountPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

function getInitials(firstName, lastName, email) {
  const first = (firstName || "").trim().charAt(0);
  const last = (lastName || "").trim().charAt(0);

  if (first || last) {
    return `${first}${last}`.toUpperCase();
  }

  return (email || "A").trim().charAt(0).toUpperCase();
}

function formatDate(value, locale) {
  if (!value) return null;

  try {
    return new Intl.DateTimeFormat(locale || "en", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

export default function AdminAccountPage() {
  const { t, i18n } = useTranslation("admin-account");
  const { getAccessTokenSilently } = useAuth0();
  const { showToast } = useToast();

  const { setUnsavedChanges, clearUnsavedChanges } = useUnsavedChanges();

  const [account, setAccount] = useState(null);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const fetchAccount = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(`${API_BASE}/api/admin/me/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload?.detail || t("errors.load"));
      }

      const data = payload?.admin || payload;

      setAccount(data);

      setForm({
        first_name: data?.first_name || "",
        last_name: data?.last_name || "",
      });
    } catch (err) {
      console.error("Could not load admin account:", err);
      setError(err.message || t("errors.load"));
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, t]);

  useEffect(() => {
    fetchAccount();
  }, [fetchAccount]);

  const hasUnsavedChanges = useMemo(() => {
    if (!account || !isEditing) return false;

    return (
      form.first_name.trim() !== (account.first_name || "").trim() ||
      form.last_name.trim() !== (account.last_name || "").trim()
    );
  }, [account, form, isEditing]);

  useEffect(() => {
    if (!account) {
      setUnsavedChanges(false);
      return;
    }

    setUnsavedChanges(hasUnsavedChanges, () => {
      setForm({
        first_name: account.first_name || "",
        last_name: account.last_name || "",
      });

      setIsEditing(false);
    });

    return () => {
      setUnsavedChanges(false);
    };
  }, [account, hasUnsavedChanges, setUnsavedChanges]);

  const handleStartEditing = () => {
    setForm({
      first_name: account?.first_name || "",
      last_name: account?.last_name || "",
    });

    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setForm({
      first_name: account?.first_name || "",
      last_name: account?.last_name || "",
    });

    setIsEditing(false);
    clearUnsavedChanges();
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    const firstName = form.first_name.trim();
    const lastName = form.last_name.trim();

    if (!firstName || !lastName) {
      showToast({
        type: "error",
        title: t("toast.validationTitle"),
        message: t("toast.validationMessage"),
      });
      return;
    }

    setSaving(true);

    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(`${API_BASE}/api/admin/me/`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload?.detail || t("errors.save"));
      }

      const updated = payload?.admin || payload;

      const nextAccount = {
        ...account,
        ...updated,
        first_name: updated?.first_name ?? firstName,
        last_name: updated?.last_name ?? lastName,
      };

      setAccount(nextAccount);

      setForm({
        first_name: nextAccount.first_name,
        last_name: nextAccount.last_name,
      });

      setIsEditing(false);
      clearUnsavedChanges();

      showToast({
        type: "success",
        title: t("toast.savedTitle"),
        message: t("toast.savedMessage"),
      });

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    } catch (err) {
      console.error("Could not update admin account:", err);

      showToast({
        type: "error",
        title: t("toast.saveFailedTitle"),
        message: err.message || t("errors.save"),
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!account?.email) return;

    setSendingPasswordReset(true);

    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(`${API_BASE}/api/admin/me/password-reset/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload?.detail || t("errors.passwordReset"));
      }

      showToast({
        type: "success",
        title: t("toast.passwordResetTitle"),
        message: t("toast.passwordResetMessage", {
          email: account.email,
        }),
      });
    } catch (err) {
      console.error("Could not request password reset:", err);

      showToast({
        type: "error",
        title: t("toast.passwordResetFailedTitle"),
        message: err.message || t("errors.passwordReset"),
      });
    } finally {
      setSendingPasswordReset(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-account-page">
        <div className="admin-account-loading">
          <Loader2 size={28} />
          <span>{t("loading")}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-account-page">
        <div className="admin-account-error">
          <AlertTriangle size={22} />

          <div>
            <strong>{t("errors.loadTitle")}</strong>

            <span>{error}</span>
          </div>

          <button type="button" onClick={fetchAccount}>
            {t("actions.retry")}
          </button>
        </div>
      </div>
    );
  }

  if (!account) return null;

  const initials = getInitials(
    account.first_name,
    account.last_name,
    account.email,
  );

  const organisation =
    account.organisation?.name ||
    account.organisation_name ||
    t("common.platformOrganisation");

  const role = account.role_display || t("common.platformAdministrator");

  const lastLogin = formatDate(account.last_login, i18n.language);

  return (
    <div className="admin-account-page">
      <header className="admin-account-header">
        <span className="admin-account-header__eyebrow">
          {t("header.eyebrow")}
        </span>

        <h1>{t("header.title")}</h1>

        <p>{t("header.subtitle")}</p>
      </header>

      <div className="admin-account-layout">
        <section className="admin-account-card admin-account-card--profile">
          <div className="admin-account-card__heading">
            <div className="admin-account-card__heading-icon">
              <UserRound size={22} />
            </div>

            <div>
              <h2>{t("profile.title")}</h2>
              <p>{t("profile.subtitle")}</p>
            </div>

            {!isEditing && (
              <button
                type="button"
                className="admin-account-edit-button"
                onClick={handleStartEditing}
              >
                <Edit3 size={17} />
                {t("actions.editProfile")}
              </button>
            )}
          </div>

          <div className="admin-account-identity">
            <div className="admin-account-avatar">{initials}</div>

            <div className="admin-account-identity__text">
              <strong>
                {account.first_name} {account.last_name}
              </strong>

              <span>{t("common.platformAdministrator")}</span>

              <small>{account.email}</small>
            </div>
          </div>

          <div
            className={`admin-account-profile-fields ${
              isEditing ? "is-editing" : ""
            }`}
          >
            <div className="admin-account-field">
              <span>{t("profile.firstName")}</span>

              {isEditing ? (
                <input
                  type="text"
                  name="first_name"
                  value={form.first_name}
                  onChange={handleFieldChange}
                  maxLength={150}
                  autoComplete="given-name"
                />
              ) : (
                <strong>{account.first_name || "—"}</strong>
              )}
            </div>

            <div className="admin-account-field">
              <span>{t("profile.lastName")}</span>

              {isEditing ? (
                <input
                  type="text"
                  name="last_name"
                  value={form.last_name}
                  onChange={handleFieldChange}
                  maxLength={150}
                  autoComplete="family-name"
                />
              ) : (
                <strong>{account.last_name || "—"}</strong>
              )}
            </div>

            <div className="admin-account-field admin-account-field--readonly">
              <span>{t("profile.email")}</span>

              <strong>{account.email || "—"}</strong>

              <small>{t("profile.emailHelp")}</small>
            </div>

            <div className="admin-account-field admin-account-field--readonly">
              <span>{t("profile.role")}</span>
              <strong>{role}</strong>
            </div>

            <div className="admin-account-field admin-account-field--wide admin-account-field--readonly">
              <span>{t("profile.organisation")}</span>

              <strong>{organisation}</strong>
            </div>
          </div>

          {isEditing && (
            <div className="admin-account-edit-actions">
              <button
                type="button"
                className="admin-account-button admin-account-button--secondary"
                onClick={handleCancelEditing}
                disabled={saving}
              >
                <X size={17} />
                {t("actions.cancel")}
              </button>

              <button
                type="button"
                className="admin-account-button admin-account-button--primary"
                onClick={handleSave}
                disabled={saving || !hasUnsavedChanges}
              >
                {saving ? (
                  <Loader2 size={17} className="admin-account-spinner" />
                ) : (
                  <Save size={17} />
                )}

                {saving ? t("actions.saving") : t("actions.saveChanges")}
              </button>
            </div>
          )}
        </section>

        <section className="admin-account-card admin-account-card--security">
          <div className="admin-account-card__heading">
            <div className="admin-account-card__heading-icon">
              <ShieldCheck size={22} />
            </div>

            <div>
              <h2>{t("security.title")}</h2>
              <p>{t("security.subtitle")}</p>
            </div>
          </div>

          <div className="admin-account-security-list">
            <div className="admin-account-security-row">
              <div className="admin-account-security-row__icon">
                <CheckCircle2 size={18} />
              </div>

              <span>{t("security.accountStatus")}</span>

              <strong
                className={
                  account.is_active === false
                    ? "admin-account-security-value"
                    : "admin-account-security-value admin-account-security-value--success"
                }
              >
                {account.is_active !== false && <Check size={15} />}

                {account.is_active === false
                  ? t("security.inactive")
                  : t("security.active")}
              </strong>
            </div>

            <div className="admin-account-security-row">
              <div className="admin-account-security-row__icon">
                <Mail size={18} />
              </div>

              <span>{t("security.emailVerification")}</span>

              <strong
                className={
                  account.email_verified
                    ? "admin-account-security-value admin-account-security-value--success"
                    : "admin-account-security-value"
                }
              >
                {account.email_verified && <Check size={15} />}

                {account.email_verified
                  ? t("security.verified")
                  : t("security.notVerified")}
              </strong>
            </div>

            <div className="admin-account-security-row">
              <div className="admin-account-security-row__icon">
                <LockKeyhole size={18} />
              </div>

              <span>{t("security.mfa")}</span>

              <strong className="admin-account-security-value admin-account-security-value--success">
                <ShieldCheck size={15} />
                {t("security.required")}
              </strong>
            </div>

            <div className="admin-account-security-row">
              <div className="admin-account-security-row__icon">
                <KeyRound size={18} />
              </div>

              <span>{t("security.provider")}</span>

              <strong>Auth0</strong>
            </div>

            {lastLogin && (
              <div className="admin-account-security-row">
                <div className="admin-account-security-row__icon">
                  <UserRound size={18} />
                </div>

                <span>{t("security.lastLogin")}</span>

                <strong>{lastLogin}</strong>
              </div>
            )}
          </div>

          <div className="admin-account-security-action">
            <div>
              <strong>{t("security.passwordTitle")}</strong>

              <p>{t("security.passwordText")}</p>
            </div>

            <button
              type="button"
              className="admin-account-button admin-account-button--secondary"
              onClick={handlePasswordReset}
              disabled={sendingPasswordReset}
            >
              {sendingPasswordReset ? (
                <Loader2 size={17} className="admin-account-spinner" />
              ) : (
                <KeyRound size={17} />
              )}

              {sendingPasswordReset
                ? t("actions.sending")
                : t("actions.changePassword")}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
